<script setup lang="ts">
  import {
    computed,
    ref,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
    toRef,
    inject,
    type Ref,
    type CSSProperties,
  } from 'vue';

  import type {
    FormElement,
    TableElement,
    TableCell as TableCellType,
  } from '../../../model/form.types';
  import { useTableKeyboardNavigation } from '../../../composables/useTableKeyboardNavigation';
  import { useTableResize } from '../../../composables/useTableResize';
  import { useTableRowHeights } from '../../../composables/useTableRowHeights';
  import { useTableCellSelection } from '../../../composables/useTableCellSelection';
  import { useTableGridSync } from '../../../composables/useTableGridSync';
  import { useTableHover } from '../../../composables/useTableHover';
  // useTablePageConstraints removed: tables now break across pages instead of
  // clamping row growth to a single page.
  import { useTableButtonPositioning } from '../../../composables/useTableButtonPositioning';
  import { useTableFieldBadges } from '../../../composables/useTableFieldBadges';
  import type {
    FieldBadgeConfiguration,
    FieldBadgeInset,
  } from '../../../lib/field-badge-anchor';
  import { cellDecorationDepth } from '../../../lib/cell-border-geometry';
  import { isRulesSelectableType } from '../../../lib/rules-field-eligibility';
  import {
    isSelectionInteractionMode,
    type FormBuilderInteractionMode,
  } from '../../../lib/interaction-mode';
  import { useTableSelectionState } from '../../../composables/useTableSelectionState';
  import {
    useTablePageBreaks,
    resolveTableRowHeights,
    type TablePageGeometry,
  } from '../../../composables/useTablePageBreaks';
  import {
    removeFormBuilderTableCellElement,
    removeFormBuilderTableCellElements,
  } from '../../../composables/removeFormBuilderCanvasElement';
  import TableCell from './TableCell.vue';
  import TableAddButton from './TableAddButton.vue';
  import TableSelectLineButton from './TableSelectLineButton.vue';
  import TableDragButton from './TableDragButton.vue';
  import { TABLE_CONSTRAINTS } from '../../../lib/table-constants';
  import { isKeyboardEventFromEditableTarget } from '../../../lib/isKeyboardEventFromEditableTarget';
  import { getResponsiveColumnTemplate } from '../../../lib/table-column-utils';
  import { useTableEditorStore } from '../../../model/table-editor.store';
  import { useFormRulesStore } from '../../../model/form-rules.store';
  import { useFormPermissionsStore } from '../../../model/form-permissions.store';
  import { useFormPermissionsSelectionStore } from '../../../model/form-permissions-selection.store';
  import { useFormBuilderAnchorFieldIds } from '../../../model/useFormBuilderAnchorFields';
  import {
    isAllCellsSelected,
    getFullySelectedLines,
  } from '../../../lib/table-selection-utils';
  import { useModal } from 'vue-final-modal';
  import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui';
  import TableCellSelectionMenu from './menus/TableCellSelectionMenu.vue';
  import TableMenu from './menus/TableMenu.vue';
  import RulesSelectionMenu from '../../build/RulesSelectionMenu.vue';
  import FieldConfigurationBadge from '../elements/FieldConfigurationBadge.vue';
  import TableDeleteConfirmModal from './menus/TableDeleteConfirmModal.vue';
  import { useRuleAwareElementDeletion } from '../../../composables/useRuleAwareElementDeletion';
  import { useFormBuilderSidebars } from '../../../composables/useFormBuilderSidebars';

  type GridCell = { row: number; col: number };

  interface Props {
    tableElement: TableElement;
    isEditable?: boolean;
    isSelected?: boolean;
    selectedCell?: GridCell | null;
    selectedCells?: GridCell[];
    anchorCell?: GridCell | null;
    activeCell?: GridCell | null;
    interactionMode?: FormBuilderInteractionMode;
    showAnchorHighlights?: boolean;
    showReportHighlights?: boolean;
    rulesMenuCell?: { row: number; col: number } | null;
    rulesMenuScope?: { scope: 'row' | 'column' | 'table'; index?: number } | null;
    showRulesAddRule?: boolean;
  }

  interface Emits {
    (e: 'select-cell', row: number, col: number, additive?: boolean, shiftKey?: boolean): void;
    (
      e: 'select-cell-range',
      startRow: number,
      startCol: number,
      endRow: number,
      endCol: number
    ): void;
    (e: 'select-row', rowIndex: number): void;
    (e: 'select-column', colIndex: number): void;
    (e: 'select-all'): void;
    (e: 'add-row'): void;
    (e: 'add-column'): void;
    (e: 'select-content', row: number, col: number, event: MouseEvent | KeyboardEvent): void;
    (e: 'scope-row', rowIndex: number, event?: MouseEvent): void;
    (e: 'scope-column', colIndex: number): void;
    (e: 'scope-table', event?: MouseEvent): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    isEditable: false,
    isSelected: false,
    selectedCell: null,
    selectedCells: () => [],
    anchorCell: null,
    activeCell: null,
    interactionMode: 'builder',
    rulesMenuCell: null,
    rulesMenuScope: null,
    showRulesAddRule: false,
  });

  const layoutVersion = ref(0);
  const emit = defineEmits<Emits>();
  const { handleTabNavigation, handleArrowNavigation } = useTableKeyboardNavigation();

  const gridRef = ref<HTMLElement | null>(null);
  const gridWrapperRef = ref<HTMLElement | null>(null);
  const abortController = ref<AbortController | null>(null);

  const tableElementRef = toRef(props, 'tableElement');
  const isEditableRef = toRef(props, 'isEditable');

  const getTablePageGeometry = inject<((element: TableElement) => TablePageGeometry | null) | null>(
    'getTablePageGeometry',
    null
  );
  const requestPaginationUpdate = inject<(() => void) | null>('requestPaginationUpdate', null);
  const paginationTick = inject<Ref<number> | null>('paginationTick', null);

  const { breaks, hasBreaks, gapRowsBefore, gapBeforeRowMap } = useTablePageBreaks(
    tableElementRef,
    () => {
      // Reading these counters registers them as reactive deps, so the grid's real top is
      // re-measured whenever the layout shifts (own grid resize, grid-sync, or a pagination
      // tick from elements above moving the table). The sum is otherwise unused.
      const revision = layoutVersion.value + (paginationTick?.value ?? 0);
      if (!getTablePageGeometry || revision < 0) return null;
      return getTablePageGeometry(props.tableElement);
    }
  );

  // Rows that sit at a chunk boundary: the last row before a break (chunk bottom)
  // and the first row after a break (chunk top). These get a closing outer border
  // so each chunk reads as its own self-contained table.
  const chunkTopRows = computed(() => new Set(breaks.value.map((brk) => brk.beforeRow)));
  const chunkBottomRows = computed(() => new Set(breaks.value.map((brk) => brk.beforeRow - 1)));

  // Vertical band of each chunk within the grid (== the wrapper, whose first child is the
  // grid at offsetTop 0), derived from the same heights + gap px that place the gap tracks,
  // so a per-chunk add-column button lines up exactly with its chunk instead of the single
  // full-height strip spanning the page break.
  const columnAddButtonBands = computed<{ top: number; height: number }[]>(() => {
    if (!hasBreaks.value) return [];

    const heights = resolveTableRowHeights(props.tableElement);
    const rows = props.tableElement.properties.rows;
    if (heights.length !== rows) return [];

    const breakRows = breaks.value.map((brk) => brk.beforeRow);
    const starts = [0, ...breakRows];
    const ends = [...breakRows, rows];

    return starts.map((start, index) => {
      const end = ends[index];
      let top = 0;
      for (let k = 0; k < start; k++) top += heights[k];
      for (const brk of breaks.value) {
        if (brk.beforeRow <= start) top += brk.gapPx;
      }
      let height = 0;
      for (let k = start; k < end; k++) height += heights[k];
      return { top, height };
    });
  });

  const {
    resizing,
    resizeTooltip,
    startColumnResize,
    startRowResize,
    handleMouseMove: handleResizeMouseMove,
    handleMouseUp: handleResizeMouseUp,
    getColumnResizeHandleStyle,
  } = useTableResize(tableElementRef, isEditableRef, gridRef, layoutVersion);

  const {
    actualRowHeights,
    calculateActualRowHeights,
    getRowResizeHandleStyle,
    convertFractionalUnitsToPixels,
  } = useTableRowHeights(tableElementRef, gridRef, resizing);

  const { dragging, handleCellMouseDown, handleCellMouseEnter, handleCellSelect, stopDragging } =
    useTableCellSelection(isEditableRef, emit);

  const {
    hoveredVisualRow,
    hoveredVisualCol,
    handleCellHover,
    handleCellMouseMove,
    isRowButtonVisible,
    isColButtonVisible,
  } = useTableHover(tableElementRef, actualRowHeights, gridRef);

  const { getRowSelectButtonStyle, getColSelectButtonStyle } = useTableButtonPositioning(
    tableElementRef,
    actualRowHeights,
    gridRef,
    resizing,
    layoutVersion
  );

  const selectedCellsRef = computed(() => props.selectedCells ?? []);
  const { isRowSelected, isColumnSelected, isAllSelected } = useTableSelectionState(
    tableElementRef,
    selectedCellsRef
  );

  const tableEditorStore = useTableEditorStore();
  const formRulesStore = useFormRulesStore();
  const formPermissionsStore = useFormPermissionsStore();
  const permissionsSelectionStore = useFormPermissionsSelectionStore();
  const { requestRuleAwareDeletion } = useRuleAwareElementDeletion();
  const { evaluateFloatingBarCollapse, resetFloatingBarOverrides } = useFormBuilderSidebars();
  const formBuilderZoom = inject<Ref<number> | null>('formBuilderZoom', null);

  const rowMenuOpen = ref<number | null>(null);
  const colMenuOpen = ref<number | null>(null);
  const tableMenuOpen = ref(false);
  const cellSelectionMenuRef = ref<HTMLElement | null>(null);
  const cellSelectionMenuHeight = ref(0);

  const injectedAnchorFieldIds = useFormBuilderAnchorFieldIds();

  const isRulesMode = computed(() => props.interactionMode === 'rules');
  const isBuilderMode = computed(() => props.interactionMode === 'builder');
  const isSelectionMode = computed(() => isSelectionInteractionMode(props.interactionMode));
  const isPermissionsMode = computed(() => props.interactionMode === 'permissions');
  const canMarkAnchorFields = computed(
    () => isRulesMode.value || injectedAnchorFieldIds.value !== null
  );

  function isFieldAnchorConfigured(fieldId: string): boolean {
    const providedIds = injectedAnchorFieldIds.value;
    return providedIds
      ? providedIds.includes(fieldId)
      : formRulesStore.isFieldAnchorConfigured(fieldId);
  }
  const canEditTable = computed(() => props.isEditable && isBuilderMode.value);
  const canShowScopeSelectors = computed(() => canEditTable.value || isSelectionMode.value);
  const canUseRulesScopeHover = computed(() => isSelectionMode.value);

  const showCellSelectionMenu = computed(() => {
    if (!canEditTable.value) return false;
    if (rowMenuOpen.value !== null || colMenuOpen.value !== null) return false;

    const cells = props.selectedCells ?? [];
    if (cells.length < 1) return false;

    return true;
  });

  // Derive full-row / full-col selection from the (small) selected-cells array instead of
  // scanning every row×col — O(selectionSize × cols), which matters a lot at 1000 rows.
  const selectionAnalysis = computed(() => {
    const tProps = props.tableElement.properties;
    return getFullySelectedLines(
      tProps.cells,
      props.selectedCells ?? [],
      tProps.rows,
      tProps.columns
    );
  });

  const selectedFullRows = computed(() => selectionAnalysis.value.fullRows);
  const selectedFullCols = computed(() => selectionAnalysis.value.fullCols);

  const floatingSelectionContext = computed(() => {
    const table = props.tableElement;
    const cells = props.selectedCells ?? [];

    if (cells.length === 0) {
      return {
        context: 'selection' as const,
        rows: [] as number[],
        cols: [] as number[],
      };
    }

    if (isAllCellsSelected(table.properties.cells, cells)) {
      return {
        context: 'table' as const,
        rows: [] as number[],
        cols: [] as number[],
      };
    }

    const fullRows = [...selectedFullRows.value];
    const fullCols = [...selectedFullCols.value];

    if (fullRows.length > 0 && cells.every((cell) => fullRows.includes(cell.row))) {
      return {
        context: 'row' as const,
        rows: fullRows,
        cols: [] as number[],
      };
    }

    if (fullCols.length > 0 && cells.every((cell) => fullCols.includes(cell.col))) {
      return {
        context: 'column' as const,
        rows: [] as number[],
        cols: fullCols,
      };
    }

    return {
      context: 'selection' as const,
      rows: [] as number[],
      cols: [] as number[],
    };
  });

  const floatingMenuVariant = computed(() => {
    if ((props.selectedCells ?? []).length === 1) return 'cell' as const;

    switch (floatingSelectionContext.value.context) {
      case 'row':
        return 'row' as const;
      case 'column':
        return 'column' as const;
      case 'table':
        return 'table' as const;
      default:
        return 'multi' as const;
    }
  });

  const getCenteredMenuStyle = () => ({
    top: '-48px',
    left: '50%',
    transform: 'translateX(-50%)',
  });

  const cellSelectionMenuStyle = computed(() => {
    if (!showCellSelectionMenu.value) return getCenteredMenuStyle();

    const wrapperElement = gridWrapperRef.value;
    const cells = props.selectedCells ?? [];
    const menuHeight = cellSelectionMenuHeight.value || 40;

    if (!wrapperElement || cells.length === 0) {
      return getCenteredMenuStyle();
    }

    const wrapperRect = wrapperElement.getBoundingClientRect();
    if (wrapperRect.width <= 0) {
      return getCenteredMenuStyle();
    }

    const gridElement = gridRef.value;
    if (!gridElement) {
      return getCenteredMenuStyle();
    }

    // Center the bar on the bounding box of the selected cells, whatever the context is:
    // a single cell, an arbitrary multi-cell range, a full row, a full column, or the
    // whole table. Since a full row's selected cells span the row (and likewise for column
    // and table), the bounding-box center is the context center. No horizontal clamping —
    // the bar is allowed to overflow the canvas edge so it stays exactly centered, and
    // dropping the clamp keeps the position independent of the bar's own width.
    const selectedElements = cells
      .map((cell) => {
        return gridElement.querySelector(
          `[data-row-index="${cell.row}"][data-col-index="${cell.col}"]`
        ) as HTMLElement | null;
      })
      .filter((el): el is HTMLElement => !!el);

    if (selectedElements.length === 0) {
      return getCenteredMenuStyle();
    }

    const rects = selectedElements.map((el) => el.getBoundingClientRect());
    const minLeft = Math.min(...rects.map((rect) => rect.left - wrapperRect.left));
    const maxRight = Math.max(...rects.map((rect) => rect.right - wrapperRect.left));
    const minTop = Math.min(...rects.map((rect) => rect.top - wrapperRect.top));

    const centerX = (minLeft + maxRight) / 2;

    const topOffset = 8;
    const minTopOffset = -56;
    const computedTop = Math.max(minTop - topOffset - menuHeight, minTopOffset);

    return {
      top: `${computedTop}px`,
      left: `${centerX}px`,
      transform: 'translateX(-50%)',
    };
  });

  watch(
    () => [isRulesMode.value, props.isSelected, (props.selectedCells ?? []).length] as const,
    ([rulesMode, isSelected, selectedCount]) => {
      if (!rulesMode) return;

      if (!isSelected && selectedCount === 0) {
        rowMenuOpen.value = null;
        colMenuOpen.value = null;
        tableMenuOpen.value = false;
      }
    }
  );

  const measureCellSelectionMenu = () => {
    const menuElement = cellSelectionMenuRef.value;
    if (!menuElement) return;
    cellSelectionMenuHeight.value = menuElement.offsetHeight;
  };

  // Measure the bar (so the vertical offset uses its real height instead of the fallback,
  // avoiding a click-to-click jump) and then collapse whichever sidebar the bar's live,
  // post-transform rect overlaps. Reads getBoundingClientRect at call time, so it reflects
  // the current zoom and shares the sidebars' viewport coordinate space.
  const runFloatingBarCollapse = async () => {
    if (!showCellSelectionMenu.value) return;
    await nextTick();
    measureCellSelectionMenu();
    const menuElement = cellSelectionMenuRef.value;
    if (menuElement) {
      evaluateFloatingBarCollapse(menuElement.getBoundingClientRect());
    }
  };

  // A) Selection changed (any cell/table, including moving between two single cells) or the
  // menu appeared/disappeared: re-arm the manual-override guard so a sidebar the user had
  // re-opened for the previous selection can auto-collapse again for this one, then evaluate.
  watch(
    () =>
      [
        selectedCellsRef.value.map((cell) => `${cell.row}:${cell.col}`).join('|'),
        floatingMenuVariant.value,
      ] as const,
    () => {
      resetFloatingBarOverrides();
      runFloatingBarCollapse();
    },
    { immediate: true }
  );

  // B) The bar moved without the selection changing — zoom (CSS scale, invisible to
  // layoutVersion) or a layout/resize reflow. Re-evaluate without re-arming, so a manual
  // re-open under the same selection is respected.
  watch([() => formBuilderZoom?.value ?? 1, layoutVersion], () => {
    runFloatingBarCollapse();
  });

  const handleRowMenuOpen = (rowIndex: number, event?: MouseEvent) => {
    if (isRulesMode.value || isPermissionsMode.value) {
      emit('scope-row', rowIndex, event);
      return;
    }

    // Builder mode: selecting a row via its handle just selects the row and lets the shared
    // floating bar appear centered over it (variant 'row'), instead of opening a separate
    // handle-anchored popover. This keeps every context on one menu that never jumps.
    if (!isRowSelected(rowIndex)) {
      emit('select-row', rowIndex);
    }
    rowMenuOpen.value = null;
    colMenuOpen.value = null;
    tableMenuOpen.value = false;
  };

  const handleColMenuOpen = (colIndex: number) => {
    if (isRulesMode.value) {
      emit('scope-column', colIndex);
      return;
    }

    // Builder mode: see handleRowMenuOpen — selecting a column shows the shared floating bar
    // centered over the column (variant 'column') rather than a handle-anchored popover.
    if (!isColumnSelected(colIndex)) {
      emit('select-column', colIndex);
    }
    rowMenuOpen.value = null;
    colMenuOpen.value = null;
    tableMenuOpen.value = false;
  };

  const handleTableMenuOpen = (event?: MouseEvent) => {
    if (isRulesMode.value || isPermissionsMode.value) {
      emit('scope-table', event);
      return;
    }

    emit('select-all');
    tableMenuOpen.value = true;
  };

  const handlePopoverOpenChange = (open: boolean, closeBuilderMenu?: () => void) => {
    if (open) return;

    if (isRulesMode.value) {
      formRulesStore.clearSelection();
      return;
    }

    closeBuilderMenu?.();
  };

  const handleAddRowAbove = () => {
    const targetRow =
      rowMenuOpen.value ??
      (floatingSelectionContext.value.context === 'row'
        ? Math.min(...floatingSelectionContext.value.rows)
        : null);
    if (targetRow === null) return;
    tableEditorStore.insertRowAt(targetRow, 'above');
    rowMenuOpen.value = null;
  };

  const handleAddRowBelow = () => {
    const targetRow =
      rowMenuOpen.value ??
      (floatingSelectionContext.value.context === 'row'
        ? Math.max(...floatingSelectionContext.value.rows)
        : null);
    if (targetRow === null) return;
    tableEditorStore.insertRowAt(targetRow, 'below');
    rowMenuOpen.value = null;
  };

  const handleAddColumnLeft = () => {
    const targetCol =
      colMenuOpen.value ??
      (floatingSelectionContext.value.context === 'column'
        ? Math.min(...floatingSelectionContext.value.cols)
        : null);
    if (targetCol === null) return;
    tableEditorStore.insertColumnAt(targetCol, 'left');
    colMenuOpen.value = null;
  };

  const handleAddColumnRight = () => {
    const targetCol =
      colMenuOpen.value ??
      (floatingSelectionContext.value.context === 'column'
        ? Math.max(...floatingSelectionContext.value.cols)
        : null);
    if (targetCol === null) return;
    tableEditorStore.insertColumnAt(targetCol, 'right');
    colMenuOpen.value = null;
  };

  const handleMergeCells = () => {
    const cells = tableEditorStore.selectedCells;
    if (cells.length > 1) {
      tableEditorStore.mergeCells([...cells]);
    }
    rowMenuOpen.value = null;
    colMenuOpen.value = null;
  };

  const handleUnmergeCells = () => {
    const cells = tableEditorStore.selectedCellsData;
    const mergeGroupIds = [
      ...new Set(cells.map((c) => c.mergeGroupId).filter((id): id is string => !!id)),
    ];
    if (mergeGroupIds.length > 0) {
      tableEditorStore.unmergeMultipleCells(mergeGroupIds);
    }
    rowMenuOpen.value = null;
    colMenuOpen.value = null;
  };

  const closeTableMenus = () => {
    rowMenuOpen.value = null;
    colMenuOpen.value = null;
    tableMenuOpen.value = false;
  };

  const openDeleteModal = (
    context: 'row' | 'column' | 'table' | 'selection',
    onConfirm: () => void
  ) => {
    const { open, close } = useModal({
      component: TableDeleteConfirmModal,
      attrs: {
        context,
        onClose() {
          close();
        },
        onConfirm() {
          close();
          onConfirm();
          closeTableMenus();
        },
      },
    });
    open();
  };

  const getRowIndexesForDeletion = (): number[] => {
    if (rowMenuOpen.value !== null) {
      return [rowMenuOpen.value];
    }
    if (floatingSelectionContext.value.context === 'row') {
      return [...floatingSelectionContext.value.rows];
    }
    return [];
  };

  const getColumnIndexesForDeletion = (): number[] => {
    if (colMenuOpen.value !== null) {
      return [colMenuOpen.value];
    }
    if (floatingSelectionContext.value.context === 'column') {
      return [...floatingSelectionContext.value.cols];
    }
    return [];
  };

  const handleDeleteRow = () => {
    const rowIndexes = getRowIndexesForDeletion();
    if (rowIndexes.length === 0) return;

    const deleteRows = () => {
      rowIndexes
        .sort((a, b) => b - a)
        .forEach((rowIndex) => tableEditorStore.deleteRowAt(rowIndex));
    };
    const rowElements = rowIndexes
      .flatMap((rowIndex) => props.tableElement.properties.cells[rowIndex] ?? [])
      .map((cell) => cell.content)
      .filter((element): element is FormElement => element !== undefined);

    if (
      requestRuleAwareDeletion(rowElements, () => {
        deleteRows();
        closeTableMenus();
      })
    ) {
      return;
    }

    openDeleteModal('row', deleteRows);
  };

  const handleDeleteColumn = () => {
    const colIndexes = getColumnIndexesForDeletion();
    if (colIndexes.length === 0) return;

    const deleteColumns = () => {
      colIndexes
        .sort((a, b) => b - a)
        .forEach((colIndex) => tableEditorStore.deleteColumnAt(colIndex));
    };
    const columnElements = props.tableElement.properties.cells
      .flatMap((row) => colIndexes.map((colIndex) => row[colIndex]?.content))
      .filter((element): element is FormElement => element !== undefined);

    if (
      requestRuleAwareDeletion(columnElements, () => {
        deleteColumns();
        closeTableMenus();
      })
    ) {
      return;
    }

    openDeleteModal('column', deleteColumns);
  };

  const handleDeleteTable = () => {
    if (
      requestRuleAwareDeletion(props.tableElement, () => {
        tableEditorStore.deleteTable();
      })
    ) {
      return;
    }
    openDeleteModal('table', () => {
      tableEditorStore.deleteTable();
    });
  };

  const handleDeleteCellElements = () => {
    const selectedElements = tableEditorStore.selectedCellsData
      .map((cell) => cell.content)
      .filter((element): element is FormElement => element !== undefined);

    if (
      requestRuleAwareDeletion(selectedElements, () => removeFormBuilderTableCellElements(false))
    ) {
      return;
    }
    removeFormBuilderTableCellElements();
  };

  const handleDeleteCellElement = () => {
    const content = tableEditorStore.selectedCellData?.content;
    if (
      content &&
      requestRuleAwareDeletion(content, () => removeFormBuilderTableCellElement(false))
    ) {
      return;
    }
    removeFormBuilderTableCellElement();
  };

  const selectionOverlayStyle = computed(() => {
    if (!canEditTable.value || !gridRef.value) return null;
    const cells = props.selectedCells ?? [];
    if (cells.length === 0) return null;

    const tProps = props.tableElement.properties;
    const allSelected = isAllCellsSelected(tProps.cells, cells);

    const hasFullRow = !allSelected && selectedFullRows.value.size > 0;
    const hasFullCol = !allSelected && selectedFullCols.value.size > 0;

    if (!allSelected && !hasFullRow && !hasFullCol) return null;

    return {
      position: 'absolute' as const,
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none' as const,
      zIndex: 5,
      borderRadius: '2px',
    };
  });

  const gridTemplateColumns = computed(() => {
    const columns = Math.max(TABLE_CONSTRAINTS.MIN_COLUMNS, props.tableElement.properties.columns);
    return getResponsiveColumnTemplate(props.tableElement.properties.columnWidths, columns);
  });

  const gridTemplateRows = computed(() => {
    const rows = Math.max(TABLE_CONSTRAINTS.MIN_ROWS, props.tableElement.properties.rows);
    const rowHeights = props.tableElement.properties.rowHeights;
    const hasStoredHeights = !!rowHeights && rowHeights.length === rows;

    const rowToken = (index: number) =>
      hasStoredHeights
        ? `minmax(${Math.max(rowHeights![index], TABLE_CONSTRAINTS.MIN_ROW_HEIGHT)}px, auto)`
        : `minmax(${TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT}px, auto)`;

    if (!hasBreaks.value) {
      if (hasStoredHeights) {
        return rowHeights!.map((_, index) => rowToken(index)).join(' ');
      }
      return `repeat(${rows}, minmax(${TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT}px, auto))`;
    }

    const gapMap = gapBeforeRowMap.value;
    const tokens: string[] = [];
    for (let index = 0; index < rows; index++) {
      const gap = gapMap.get(index);
      if (gap !== undefined) {
        tokens.push(`${gap}px`);
      }
      tokens.push(rowToken(index));
    }
    return tokens.join(' ');
  });

  const containerStyles = computed<CSSProperties>(() => ({
    width: `${props.tableElement.properties.tableWidth || 100}%`,
  }));

  const canAddRows = computed(() => {
    return props.tableElement.properties.isHeader !== true;
  });

  const canAddColumns = computed(() => {
    return props.tableElement.properties.isHeader !== true;
  });

  const isRowLimitReached = computed(() => {
    return props.tableElement.properties.rows >= TABLE_CONSTRAINTS.MAX_ROWS;
  });

  const isColumnLimitReached = computed(() => {
    return props.tableElement.properties.columns >= TABLE_CONSTRAINTS.MAX_COLUMNS;
  });

  const gridStyles = computed(() => ({
    display: 'grid',
    gridTemplateColumns: gridTemplateColumns.value,
    gridTemplateRows: gridTemplateRows.value,
    gap: '0',
    position: 'relative' as const,
  }));

  const isCellSelected = (rowIndex: number, colIndex: number): boolean => {
    if (props.selectedCells && props.selectedCells.length > 0) {
      return props.selectedCells.some(
        (cell: { row: number; col: number }) => cell.row === rowIndex && cell.col === colIndex
      );
    }
    if (!props.selectedCell) return false;
    return props.selectedCell.row === rowIndex && props.selectedCell.col === colIndex;
  };

  const shouldRenderRowScopeButton = (rowIndex: number): boolean => {
    const rowCells = props.tableElement.properties.cells[rowIndex] ?? [];

    return !rowCells.some((cell) => {
      if (!cell.mergeRoot || cell.mergeRoot.row >= rowIndex) {
        return false;
      }

      return cell.isMerged === true || cell.merged === true;
    });
  };

  // Only mount the row-select Popover for rows that can actually show their button right now:
  // the hovered row, the row whose menu is open, the active rules-scope row, and any
  // fully-selected row (whose button stays visually selected without hover). At 1000 rows this
  // renders a handful of Popovers instead of ~1000, which is the main freeze fix. Hover is
  // sensed by cell mouse events (useTableHover), not by these buttons, so gating is safe.
  const activeRowScopeIndices = computed<number[]>(() => {
    const indices = new Set<number>();
    if (hoveredVisualRow.value !== null) indices.add(hoveredVisualRow.value);
    if (rowMenuOpen.value !== null) indices.add(rowMenuOpen.value);
    if (
      isRulesMode.value &&
      props.rulesMenuScope?.scope === 'row' &&
      props.rulesMenuScope.index !== undefined
    ) {
      indices.add(props.rulesMenuScope.index);
    }
    for (const row of selectedFullRows.value) indices.add(row);

    const rows = props.tableElement.properties.rows;
    return [...indices].filter(
      (row) => row >= 0 && row < rows && shouldRenderRowScopeButton(row)
    );
  });

  // Same on-demand rendering for column-select Popovers. Columns cap at 50 so this is not the
  // freeze driver, but it keeps the pattern consistent and trims mounted Popovers per table.
  const activeColScopeIndices = computed<number[]>(() => {
    const indices = new Set<number>();
    if (hoveredVisualCol.value !== null) indices.add(hoveredVisualCol.value);
    if (colMenuOpen.value !== null) indices.add(colMenuOpen.value);
    if (
      isRulesMode.value &&
      props.rulesMenuScope?.scope === 'column' &&
      props.rulesMenuScope.index !== undefined
    ) {
      indices.add(props.rulesMenuScope.index);
    }
    for (const col of selectedFullCols.value) indices.add(col);

    const columns = props.tableElement.properties.columns;
    return [...indices].filter((col) => col >= 0 && col < columns);
  });

  // A row's bottom-edge resize handle is only grabbable when the cursor is near that row, and
  // the cursor being over a cell already sets hoveredVisualRow. Render only the handles around
  // the hovered row (its own edge and the edge above it), plus keep them mounted during an
  // active drag. Reduces ~1000 absolutely-positioned divs to ~2. (1-based to match the existing
  // getRowResizeHandleStyle / startRowResize call sites.)
  const activeRowResizeHandles = computed<number[]>(() => {
    const rows = props.tableElement.properties.rows;
    const hovered = hoveredVisualRow.value;
    if (hovered === null) {
      return [];
    }
    return [hovered, hovered + 1].filter((row) => row >= 1 && row <= rows);
  });

  const isEditableTargetOutsideGrid = (event: KeyboardEvent): boolean => {
    const target = event.target;
    const isFromWithinGrid =
      target instanceof HTMLElement && target.closest('.table-grid') !== null;
    return !isFromWithinGrid && isKeyboardEventFromEditableTarget(event);
  };

  const getCurrentNavigationCell = (): GridCell | null =>
    props.activeCell ??
    props.selectedCell ??
    (props.selectedCells.length > 0 ? props.selectedCells[0] : null);

  const emitCellNavigation = (
    origin: GridCell,
    target: { newRow: number; newCol: number },
    event: KeyboardEvent,
    isTabKey: boolean
  ) => {
    const { newRow, newCol } = target;
    if (newRow === origin.row && newCol === origin.col) return;

    const useShiftSelection = event.shiftKey && !isTabKey;
    if (useShiftSelection && props.anchorCell) {
      emit('select-cell-range', props.anchorCell.row, props.anchorCell.col, newRow, newCol);
    } else {
      emit('select-cell', newRow, newCol, false, useShiftSelection);
    }
  };

  const handleKeyboardNavigation = (event: KeyboardEvent) => {
    if (!canEditTable.value) return;
    if (isEditableTargetOutsideGrid(event)) return;

    const currentCell = getCurrentNavigationCell();
    if (!currentCell) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      emit('select-cell', -1, -1);
      return;
    }

    const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);
    const isTabKey = event.key === 'Tab';
    if (!isArrowKey && !isTabKey) return;

    event.preventDefault();

    const { row, col } = currentCell;
    const { rows, columns } = props.tableElement.properties;
    const target = isTabKey
      ? handleTabNavigation(row, col, columns, rows, event.shiftKey, props.tableElement)
      : handleArrowNavigation(event.key, row, col, rows, columns, props.tableElement);

    emitCellNavigation(currentCell, target, event, isTabKey);
  };

  // Render the matrix nested (one keyed list per row) rather than a single flat list. Because
  // cells are placed by explicit grid-row/grid-column, DOM order is irrelevant to layout, and
  // <template v-for> adds no DOM box, so cells stay direct grid children. This keeps both row
  // adds (outer tail-append) and column adds (per-row inner tail-append) on Vue's fast keyed
  // diff path — a flat row-major list scatters new column cells and defeats it.
  const cellRows = computed(() => props.tableElement.properties.cells || []);

  const getCellPermissionOutline = (cell: TableCellType) => {
    if (!isPermissionsMode.value || !cell.content) return null;
    if (!isRulesSelectableType(cell.content.type)) return null;
    return permissionsSelectionStore.getOutline(
      cell.content.id,
      formPermissionsStore.isFieldPermissioned(cell.content.id),
      props.tableElement.id
    );
  };

  const isCellBadgeable = (cell: TableCellType) =>
    isRulesMode.value && !!cell.content && isRulesSelectableType(cell.content.type);

  // Anchor marking also answers to a read-only screen that handed the ids down, so it needs a
  // gate of its own. The report badge stays where it was: rules stage only.
  const isCellAnchorBadgeable = (cell: TableCellType) =>
    canMarkAnchorFields.value && !!cell.content && isRulesSelectableType(cell.content.type);

  const isCellAnchorConfigured = (cell: TableCellType) => {
    if (!props.showAnchorHighlights || !isCellAnchorBadgeable(cell)) return false;
    return isFieldAnchorConfigured(cell.content!.id);
  };

  const isCellReportConfigured = (cell: TableCellType) => {
    if (!props.showReportHighlights || !isCellBadgeable(cell)) return false;
    return formRulesStore.isFieldReportConfigured(cell.content!.id);
  };

  const cellBadgeConfiguration = (cell: TableCellType): FieldBadgeConfiguration | null => {
    if (isCellAnchorConfigured(cell)) return 'anchor';
    if (isCellReportConfigured(cell)) return 'report';
    return null;
  };

  // The badge is anchored to the cell's padding box, so it still has to clear whatever the cell
  // paints inside it: a custom border bleeds half its width inward, and an applied rule adds the
  // `.rule-highlight-overlay` ring on top of that.
  const cellBadgeInset = (cell: TableCellType): FieldBadgeInset => {
    const border = cell.properties?.border;
    // The ring itself is drawn in the rules stage only, so anywhere else there is nothing to
    // clear. Asking the shared rules store outside that stage would also offset the badge by the
    // draft of whoever is building rules, since every Registration shares the same field ids.
    const hasAppliedRule =
      isRulesMode.value && formRulesStore.hasAppliedTriggerRule(cell.content!.id);

    return {
      top: cellDecorationDepth('top', border?.top, hasAppliedRule),
      right: cellDecorationDepth('right', border?.right, hasAppliedRule),
    };
  };

  const fieldBadgeTargets = computed(() =>
    cellRows.value.flat().flatMap((cell) => {
      if (cell.isMerged) return [];

      const configuration = cellBadgeConfiguration(cell);
      if (!configuration) return [];

      return [
        {
          key: cell.id,
          rowIndex: cell.rowIndex,
          colIndex: cell.colIndex,
          configuration,
          inset: cellBadgeInset(cell),
        },
      ];
    })
  );

  const { badges: fieldBadges } = useTableFieldBadges(gridRef, fieldBadgeTargets, layoutVersion);

  const handleMouseUp = () => {
    handleResizeMouseUp();
    stopDragging();
  };

  const handleSelectColumn = (colIndex: number) => {
    emit('select-column', colIndex);
  };

  const handleSelectRow = (rowIndex: number) => {
    emit('select-row', rowIndex);
  };

  let resizeObserver: ResizeObserver | null = null;

  onMounted(() => {
    abortController.value = new AbortController();
    const { signal } = abortController.value;

    document.addEventListener('mousemove', handleResizeMouseMove, { signal });
    document.addEventListener('mouseup', handleMouseUp, { signal });
    document.addEventListener('keydown', handleKeyboardNavigation, { signal });

    nextTick(() => {
      convertFractionalUnitsToPixels();
      calculateActualRowHeights();

      if (gridRef.value) {
        let lastObservedHeight = gridRef.value.offsetHeight;
        resizeObserver = new ResizeObserver((entries) => {
          calculateActualRowHeights();
          // Only bump layoutVersion when the grid height actually changed. Otherwise the
          // ResizeObserver → layoutVersion++ → breaks → gap tracks → resize feedback loop
          // re-runs O(rows×cols) work on every settle; gating it makes the loop converge.
          const nextHeight = entries[0]?.contentRect.height ?? gridRef.value?.offsetHeight ?? 0;
          if (Math.round(nextHeight) !== Math.round(lastObservedHeight)) {
            lastObservedHeight = nextHeight;
            layoutVersion.value++;
          }
        });
        resizeObserver.observe(gridRef.value);
      }
    });
  });

  onBeforeUnmount(() => {
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  useTableGridSync(tableElementRef, {
    calculateActualRowHeights,
    onLayoutChange: () => {
      layoutVersion.value++;
    },
  });

  watch(
    () => breaks.value.map((brk) => `${brk.beforeRow}:${Math.round(brk.gapPx)}`).join('|'),
    () => {
      layoutVersion.value++;
      nextTick(() => {
        requestPaginationUpdate?.();
      });
    }
  );
</script>

<template>
  <div
    class="table-grid-container w-full font-[family-name:var(--font-base)] py-2 overflow-visible"
    :style="containerStyles"
  >
    <div
      ref="gridWrapperRef"
      class="table-grid-wrapper group relative w-min min-w-full"
      :class="{
        'is-selected': isSelected,
        'rules-mode': isRulesMode,
        'is-broken': hasBreaks,
        'bg-gray-13': isSelected && !hasBreaks && !isRulesMode,
        'bg-[var(--color-gray-08)]': isSelected && !hasBreaks && isRulesMode,
      }"
      @mouseleave="handleCellHover(null, null)"
    >
      <div
        ref="gridRef"
        class="table-grid relative w-full bg-transparent"
        :style="gridStyles"
        role="grid"
        :aria-label="`Tabela com ${props.tableElement.properties.rows} linhas e ${props.tableElement.properties.columns} colunas`"
      >
        <template
          v-for="(row, rowIndex) in cellRows"
          :key="row[0]?.id ?? `row-${rowIndex}`"
        >
          <TableCell
            v-for="cell in row"
            :key="cell.id"
            :cell="cell"
            :gap-rows-before="gapRowsBefore(cell.rowIndex)"
            :is-chunk-top="chunkTopRows.has(cell.rowIndex)"
            :is-chunk-bottom="chunkBottomRows.has(cell.rowIndex)"
            :is-selected="isCellSelected(cell.rowIndex, cell.colIndex)"
            :is-editable="canEditTable"
            :allow-scope-hover="canUseRulesScopeHover"
            :interaction-mode="interactionMode"
            :show-rules-menu="
              rulesMenuCell?.row === cell.rowIndex && rulesMenuCell?.col === cell.colIndex
            "
            :show-add-rule="
              showRulesAddRule &&
              rulesMenuCell?.row === cell.rowIndex &&
              rulesMenuCell?.col === cell.colIndex
            "
            :has-applied-rule="
              isRulesMode && cell.content
                ? formRulesStore.hasAppliedTriggerRule(cell.content.id)
                : false
            "
            :permission-outline="getCellPermissionOutline(cell)"
            :show-selection-fill="permissionsSelectionStore.fillVisible"
            :total-rows="tableElement.properties.rows"
            :total-columns="tableElement.properties.columns"
            :border-width="tableElement.properties.borderWidth"
            :border-color="tableElement.properties.borderColor"
            :border-style="tableElement.properties.borderStyle"
            :border-pattern="tableElement.properties.borderPattern"
            @select="handleCellSelect"
            @mousedown="handleCellMouseDown"
            @mouseenter="
              (row: number, col: number) => {
                handleCellMouseEnter(row, col);
                handleCellHover(row, col);
              }
            "
            @mousemove="
              (row: number, col: number, event: MouseEvent) => {
                handleCellMouseMove(row, col, event);
              }
            "
            @select-content="
              (row: number, col: number, event: MouseEvent | KeyboardEvent) => {
                emit('select-content', row, col, event);
              }
            "
            @close-rules-menu="formRulesStore.clearSelection"
          />
        </template>

        <div
          v-if="fieldBadges.length > 0"
          class="field-configuration-badges pointer-events-none absolute inset-0 z-20"
          aria-hidden="true"
        >
          <FieldConfigurationBadge
            v-for="badge in fieldBadges"
            :key="badge.key"
            :configuration="badge.configuration"
            :anchor-style="badge.style"
          />
        </div>
      </div>

      <div
        v-if="canEditTable"
        class="resize-handles absolute top-0 left-0 w-full h-full pointer-events-none"
      >
        <div
          v-for="colIndex in tableElement.properties.columns - 1"
          :key="`col-${colIndex}`"
          class="column-resize-handle absolute top-0 w-1.5 h-full -ml-[3px] cursor-col-resize pointer-events-auto z-10 transition-colors duration-200 hover:bg-[var(--color-gray-03)] hover:opacity-30 active:bg-[var(--color-gray-03)] active:opacity-50"
          :style="getColumnResizeHandleStyle(colIndex)"
          @mousedown="startColumnResize(colIndex - 1, $event)"
        />

        <div
          v-for="rowIndex in activeRowResizeHandles"
          :key="`row-${rowIndex}`"
          class="row-resize-handle absolute left-0 w-full h-1.5 -mt-[3px] cursor-row-resize pointer-events-auto z-10 transition-colors duration-200 hover:bg-[var(--color-gray-03)] hover:opacity-30 active:bg-[var(--color-gray-03)] active:opacity-50"
          :style="getRowResizeHandleStyle(rowIndex)"
          @mousedown="startRowResize(rowIndex - 1, $event)"
        />
      </div>

      <div
        v-if="resizeTooltip"
        class="pointer-events-none fixed inset-0 z-[1000] flex items-center justify-center"
      >
        <div
          class="resize-tooltip animate-in fade-in zoom-in-90 duration-150 pointer-events-none rounded-md bg-black/90 px-3 py-2.5 text-white shadow-[0_4px_6px_rgba(0,0,0,0.2)]"
        >
          <div class="tooltip-label text-[11px] uppercase tracking-[0.5px] text-white/70 mb-1">
            {{ resizeTooltip.label }}
          </div>
          <div
            class="tooltip-value text-base font-semibold font-[family-name:var(--font-base)] text-center"
          >
            {{ resizeTooltip.text }}
          </div>
        </div>
      </div>

      <div
        v-if="selectionOverlayStyle"
        :style="selectionOverlayStyle"
        class="selection-overlay"
      />

      <Popover
        v-if="canShowScopeSelectors"
        :open="isSelectionMode ? rulesMenuScope?.scope === 'table' : isAllSelected && tableMenuOpen"
        @update:open="
          (v: boolean) => {
            handlePopoverOpenChange(v, () => {
              tableMenuOpen = false;
            });
          }
        "
      >
        <PopoverTrigger as-child>
          <TableDragButton
            :is-visible="
              isRulesMode ? isSelected : hoveredVisualRow !== null || hoveredVisualCol !== null
            "
            :is-selected="isRulesMode ? rulesMenuScope?.scope === 'table' : isAllSelected"
            :tooltip-content="isSelectionMode ? 'Selecionar tabela' : 'Selecionar/mover tabela'"
            :cursor="isSelectionMode ? 'pointer' : 'move'"
            :draggable="!isSelectionMode"
            @select-all="
              (event?: MouseEvent) =>
                isSelectionMode || isAllSelected ? handleTableMenuOpen(event) : emit('select-all')
            "
          />
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          :side-offset="8"
          :disable-portal="isRulesMode"
          :disable-animations="isRulesMode"
          :position-strategy="isRulesMode ? 'absolute' : 'fixed'"
          class="w-auto p-0 table-context-popover z-40"
        >
          <RulesSelectionMenu v-if="isRulesMode && !formRulesStore.ruleItemSelectionActive" />
          <TableMenu
            v-else-if="!isSelectionMode"
            @delete-table="handleDeleteTable"
          />
        </PopoverContent>
      </Popover>

      <div
        v-if="canEditTable"
        class="col-select-buttons absolute -top-2.5 left-0 w-full h-6 pointer-events-none z-20"
      >
        <Popover
          v-for="colIndex in activeColScopeIndices"
          :key="`col-select-${colIndex}`"
          :open="
            isRulesMode
              ? rulesMenuScope?.scope === 'column' && rulesMenuScope?.index === colIndex
              : colMenuOpen === colIndex
          "
          @update:open="
            (v: boolean) => {
              handlePopoverOpenChange(v, () => {
                colMenuOpen = v ? colIndex : null;
              });
            }
          "
        >
          <PopoverTrigger as-child>
            <TableSelectLineButton
              direction="column"
              :index="colIndex"
              :is-visible="
                isRulesMode
                  ? canUseRulesScopeHover && isColButtonVisible(colIndex)
                  : isColButtonVisible(colIndex)
              "
              :is-selected="
                isRulesMode
                  ? rulesMenuScope?.scope === 'column' && rulesMenuScope?.index === colIndex
                  : !isAllSelected && isColumnSelected(colIndex)
              "
              :style="getColSelectButtonStyle(colIndex)"
              @select="handleColMenuOpen"
            />
          </PopoverTrigger>
          <PopoverContent
            :side="isRulesMode ? 'bottom' : 'top'"
            align="start"
            :align-offset="0"
            :side-offset="8"
            :disable-portal="isRulesMode"
            :disable-animations="isRulesMode"
            :position-strategy="isRulesMode ? 'absolute' : 'fixed'"
            class="w-auto p-0 table-context-popover z-40 pointer-events-auto"
          >
            <RulesSelectionMenu v-if="isRulesMode && !formRulesStore.ruleItemSelectionActive" />
            <TableCellSelectionMenu
              v-else-if="!isSelectionMode"
              variant="column"
              @add-column-left="handleAddColumnLeft"
              @add-column-right="handleAddColumnRight"
              @merge-cells="handleMergeCells"
              @unmerge-cells="handleUnmergeCells"
              @delete-column="handleDeleteColumn"
            />
          </PopoverContent>
        </Popover>
      </div>

      <div
        v-if="canShowScopeSelectors"
        class="row-select-buttons absolute top-0 -left-2.5 pointer-events-none z-[41]"
      >
        <Popover
          v-for="rowIndex in activeRowScopeIndices"
          :key="`row-select-${rowIndex}`"
          :open="
            isSelectionMode
              ? rulesMenuScope?.scope === 'row' && rulesMenuScope?.index === rowIndex
              : rowMenuOpen === rowIndex
          "
          @update:open="
            (v: boolean) => {
              handlePopoverOpenChange(v, () => {
                rowMenuOpen = v ? rowIndex : null;
              });
            }
          "
        >
          <PopoverTrigger as-child>
            <TableSelectLineButton
              direction="row"
              :index="rowIndex"
              :is-visible="
                isSelectionMode
                  ? canUseRulesScopeHover && isRowButtonVisible(rowIndex)
                  : isRowButtonVisible(rowIndex)
              "
              :is-selected="
                isSelectionMode
                  ? rulesMenuScope?.scope === 'row' && rulesMenuScope?.index === rowIndex
                  : !isAllSelected && isRowSelected(rowIndex)
              "
              :style="getRowSelectButtonStyle(rowIndex)"
              @select="handleRowMenuOpen"
            />
          </PopoverTrigger>
          <PopoverContent
            :side="isSelectionMode ? 'bottom' : 'top'"
            align="start"
            :align-offset="4"
            :side-offset="8"
            :disable-portal="isSelectionMode"
            :disable-animations="isSelectionMode"
            :position-strategy="isSelectionMode ? 'absolute' : 'fixed'"
            class="w-auto p-0 table-context-popover z-40 pointer-events-auto"
          >
            <RulesSelectionMenu v-if="isRulesMode && !formRulesStore.ruleItemSelectionActive" />
            <TableCellSelectionMenu
              v-else-if="!isSelectionMode"
              variant="row"
              @add-row-above="handleAddRowAbove"
              @add-row-below="handleAddRowBelow"
              @merge-cells="handleMergeCells"
              @unmerge-cells="handleUnmergeCells"
              @delete-row="handleDeleteRow"
            />
          </PopoverContent>
        </Popover>
      </div>

      <div
        v-if="showCellSelectionMenu"
        ref="cellSelectionMenuRef"
        class="cell-selection-floating-bar absolute z-50 pointer-events-auto bg-[var(--color-popover)] border border-[var(--color-border)] rounded-lg shadow-[0_4px_6px_-1px_rgb(0_0_0/0.1),0_2px_4px_-2px_rgb(0_0_0/0.1)]"
        :style="cellSelectionMenuStyle"
      >
        <TableCellSelectionMenu
          :variant="floatingMenuVariant"
          @add-row-above="handleAddRowAbove"
          @add-row-below="handleAddRowBelow"
          @add-column-left="handleAddColumnLeft"
          @add-column-right="handleAddColumnRight"
          @merge-cells="handleMergeCells"
          @unmerge-cells="handleUnmergeCells"
          @delete-row="handleDeleteRow"
          @delete-column="handleDeleteColumn"
          @delete-table="handleDeleteTable"
          @delete-cell-elements="handleDeleteCellElements"
          @delete-cell-element="handleDeleteCellElement"
        />
      </div>

      <template v-if="canEditTable && canAddColumns">
        <template v-if="hasBreaks">
          <TableAddButton
            v-for="(band, bandIndex) in columnAddButtonBands"
            :key="`col-add-${bandIndex}`"
            direction="column"
            :segment="band"
            :disabled="isColumnLimitReached"
            @add="emit('add-column')"
          />
        </template>
        <TableAddButton
          v-else
          direction="column"
          :disabled="isColumnLimitReached"
          @add="emit('add-column')"
        />
      </template>

      <TableAddButton
        v-if="canEditTable && canAddRows"
        direction="row"
        :disabled="isRowLimitReached"
        @add="emit('add-row')"
      />
    </div>
  </div>
</template>
